import { useCallback, useState } from 'react';
import { UtensilsCrossed } from 'lucide-react';
import { updateIikoFeature } from '../../api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Тумблер интеграции с iiko. Выключение не удаляет данные: заказы остаются в кэше и вернутся
 * в интерфейс, как только интеграцию включат обратно.
 */
const IikoIntegrationCard = ({ user }) => {
  const { toast } = useToast();
  const { features, isLoadingFeatures, refreshFeatures } = useFeatures();
  const isAdmin = user?.role === 'admin';
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = useCallback(
    async (enabled) => {
      setIsSaving(true);
      try {
        await updateIikoFeature(enabled);
        await refreshFeatures();
        toast({
          title: enabled ? 'Интеграция с iiko включена' : 'Интеграция с iiko выключена'
        });
      } catch (error) {
        toast({
          title: 'Не удалось изменить настройку',
          description: describeError(error),
          variant: 'destructive'
        });
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFeatures, toast]
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5" />
            Интеграция с iiko
            <Badge variant={features.iikoEnabled ? 'default' : 'outline'}>
              {features.iikoEnabled ? 'Включена' : 'Выключена'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Подтягивает заказы клиента по номеру телефона. Нужна только ресторанной доставке.
          </CardDescription>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="iiko-integration"
            data-ev="integrations.iiko.toggle"
            checked={features.iikoEnabled}
            disabled={isSaving || isLoadingFeatures}
            onCheckedChange={handleToggle}
          />
          <Label htmlFor="iiko-integration">Включить</Label>
        </div>
      </CardHeader>

      <CardContent className="text-sm text-muted-foreground">
        При выключении из интерфейса пропадают: колонка «Последний заказ» в «Пропущенных»,
        вкладка «Заказ» в карточке жалобы, а также всё, что связано с точкой заказа в «Жалобах» —
        колонка и фильтр «Точка», разрез «По точкам», карточка «Точка-лидер», кнопка «Обновить точки»
        и одноимённая колонка в Excel-выгрузке. Запросы в iiko при этом не отправляются вовсе.
        Данные не удаляются: включение вернёт всё на место.
      </CardContent>
    </Card>
  );
};

export default IikoIntegrationCard;
